import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { billingTabEnum } from "../../../../../util/enum";
import { getPharmacyStockBatches } from "../pharmacyStockHelpers";

const initValues = {
  batchNumber: null,
  batchId: null,
  billableQty: 1,
  uom: null,
  billedSeparately: false,
  billingStatus: "Issued",
};

const PharmacyEditConsumableDrawer = ({ open, onClose, consumable }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicDet, services, locations, facilities } =
    useMemo(() => {
      if (!mandateDataRaw) {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
          clinicDet: reqData?.clinic ?? {},
          services: Array.isArray(reqData?.services) ? reqData.services : [],
          locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
          facilities: Array.isArray(reqData?.facilities)
            ? reqData.facilities
            : [],
        };
      } catch {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
    }, [mandateDataRaw]);

  const getBatches = (stkId = null) => {
    const batchList = getPharmacyStockBatches(
      stkId,
      clinicStocks,
      clinicDet?.id,
      locations,
    );
    const reqList = batchList
      ?.filter((li) => li?.quantity > 0)
      ?.sort((a, b) => a?.quantity - b?.quantity);
    return reqList?.length > 0
      ? reqList?.map((bat) => {
          const reqLocation = locations?.find(
            (loc) => loc?.id === bat?.locationId,
          );
          return {
            name: bat?.batchNo,
            value: bat?.batchId,
            subunits: bat?.subunits,
            quantity: bat?.quantity,
            locationId: bat?.locationId,
            locationName: reqLocation?.locationName ?? "",
            uom: bat?.qtyMapName ?? "",
          };
        })
      : [
          {
            name: "Unknown",
            value: null,
            subunits: null,
            quantity: null,
            locationId: null,
            locationName: "",
            uom: "",
          },
        ];
  };

  const getBatchByStockAndBatchId = (stkId, batId) => {
    if (!stkId || !batId) return null;
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.stockBatch
        ?.filter((btch) => btch?.createdBy === clinicDet?.id)
        ?.find((bat) => bat?.batchId === batId) ?? null
    );
  };

  const buildBatchOption = (stkId, batchId = null, batchNo = null) => {
    const reqBat = batchId ? getBatchByStockAndBatchId(stkId, batchId) : null;
    const reqLocation = locations?.find(
      (loc) => loc?.id === reqBat?.locationId,
    );
    return {
      name: batchNo ?? reqBat?.batchNo ?? "Unknown",
      value: batchId ?? reqBat?.batchId ?? null,
      subunits: reqBat?.subunits ?? null,
      quantity: reqBat?.quantity ?? null,
      locationId: reqBat?.locationId ?? null,
      locationName: reqLocation?.locationName ?? "",
      uom: reqBat?.qtyMapName ?? "",
    };
  };

  const getBatchesForSelect = (
    stkId = null,
    batchId = null,
    batchNo = null,
  ) => {
    const batchList = getBatches(stkId);
    if (!batchId && !batchNo) return batchList;

    const exists = batchList?.some(
      (bat) =>
        (batchId && String(bat?.value) === String(batchId)) ||
        (batchNo && bat?.name === batchNo),
    );
    if (exists) return batchList;

    const selectedOption = buildBatchOption(stkId, batchId, batchNo);
    if (
      batchList?.length === 1 &&
      batchList?.[0]?.name === "Unknown" &&
      !batchList?.[0]?.value
    ) {
      return [selectedOption];
    }
    return [selectedOption, ...(batchList ?? [])];
  };

  const getUOMListByStockId = (stkId = null) => {
    if (!stkId) return [];
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? []
    );
  };

  const getDefaultProcedureContext = () => {
    const reqProced =
      consumable?.procedure ??
      reqDet?.procedures?.find(
        (pl) =>
          pl?.serviceId ===
          (consumable?.procedure?.serviceId ?? consumable?.serviceId),
      ) ??
      reqDet?.procedures?.[0];
    const serviceId = reqProced?.serviceId ?? consumable?.serviceId ?? null;
    const reqServ = services?.find((ser) => ser?.serviceId === serviceId);
    const reqVar = reqServ?.serviceVariants?.find(
      (vnt) =>
        String(vnt?.variantId) ===
        String(reqProced?.procedureVariantId ?? consumable?.procedureVariantId),
    );
    return { reqProced, reqServ, reqVar, serviceId };
  };

  const resolveInitialBatchNumber = (item) => {
    if (!item?.stockId) return null;

    const batchId = item?.batchId ?? null;
    const batchNo = item?.batchNo ?? null;
    const batchList = getBatchesForSelect(item?.stockId, batchId, batchNo);

    if (batchId || batchNo) {
      const match = batchList?.find(
        (bat) =>
          (batchId && String(bat?.value) === String(batchId)) ||
          (batchNo && bat?.name === batchNo),
      );
      if (match) return match;
      return buildBatchOption(item?.stockId, batchId, batchNo);
    }

    return batchList?.[0] ?? null;
  };

  const onChangeValue = (name, value) => {
    setValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "batchNumber") {
        reqObj.batchNumber = value;
        reqObj.batchId = value?.value ?? null;
      }
      return reqObj;
    });
  };

  useEffect(() => {
    if (!open) {
      setValues(initValues);
      setIsSaving(false);
      return;
    }
    if (!consumable) return;

    const isItemRequested =
      consumable?.status === "requested" ||
      consumable?.remarks === "Requested" ||
      consumable?.requestBadgeText;
    const reqUomList = getUOMListByStockId(consumable?.stockId);
    const matUom = reqUomList?.find(
      (ul) =>
        ul?.uomMappingId === consumable?.uomId ||
        ul?.value === consumable?.uomName,
    );
    const batchNumber = resolveInitialBatchNumber(consumable);
    setValues({
      batchNumber,
      batchId: batchNumber?.value ?? consumable?.batchId ?? null,
      billableQty: consumable?.quantity ?? 1,
      uom: consumable?.uomName?.length > 0 && matUom ? matUom : null,
      billedSeparately: !!consumable?.billSeparated,
      billingStatus: isItemRequested ? "Requested" : "Issued",
    });
  }, [consumable, open]);

  const onApiCall = () => {
    dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
  };

  const buildConsumableDetail = () => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === consumable?.stockId,
    );
    const selectedBatchId =
      values?.batchNumber?.value ?? consumable?.batchId ?? null;
    const reqBatDet = getBatchByStockAndBatchId(
      consumable?.stockId,
      selectedBatchId,
    );
    const uomList = getUOMListByStockId(consumable?.stockId);
    const matchedUom =
      values?.uom ??
      uomList?.find(
        (ul) =>
          ul?.uomMappingId === consumable?.uomId ||
          ul?.value === consumable?.uomName,
      );
    const batUomMatch = uomList?.find(
      (ul) =>
        ul?.uomMappingId === reqBatDet?.mapId ||
        ul?.value === reqBatDet?.uomMapName,
    );
    const linkedProcedure = false;
    const linkToProcedure = false;
    const isNoAmount = values?.billedSeparately
      ? false
      : reqStock?.autoConsumed || !reqStock?.billable;

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqBat = reqStock?.autoConsumed
      ? clinicBatches?.[0]
      : selectedBatchId
        ? (clinicBatches?.find((bat) => bat?.batchId === selectedBatchId) ??
          clinicBatches?.[0])
        : clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const stckUnitPrice =
      reqBat?.unitSellingPrice ?? clinicBatches?.[0]?.unitSellingPrice ?? 0;
    const batchConversion = Number(batUomMatch?.conversion ?? 1);
    const selectedConversion = Number(matchedUom?.conversion ?? 1);
    const itmPrice =
      batUomMatch?.uomMappingId === matchedUom?.uomMappingId
        ? Number(stckUnitPrice)
        : (Number(stckUnitPrice) * batchConversion) / selectedConversion;
    const reqUnitPrice = isNoAmount ? 0 : itmPrice;

    const { reqProced, reqServ, reqVar, serviceId } = getDefaultProcedureContext();
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const unitPrice = Number(reqUnitPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const quantity = Number(values?.billableQty) || 1;
    const markUsed = false;
    const isRequestedSave =
      consumable?.status === "requested" ||
      consumable?.remarks === "Requested" ||
      (!selectedBatchId &&
        !reqBat?.batchId &&
        consumable?.status === "requested");

    return {
      detailId: consumable?.detailId ?? null,
      stockId: consumable?.stockId ?? null,
      batchId:
        selectedBatchId ?? (reqStock?.autoConsumed ? reqBat?.batchId : null),
      type: "Consumables",
      quantity,
      status: isRequestedSave
        ? "requested"
        : markUsed
          ? true
          : reqStock?.autoConsumed
            ? consumable?.detailId
              ? consumable?.status
              : true
            : (selectedBatchId ?? reqBat?.batchId)
              ? (consumable?.status ?? true)
              : "requested",
      remarks: isRequestedSave
        ? "Requested"
        : markUsed
          ? "Requested"
          : consumable?.remarks === "Completed"
            ? "Completed"
            : (consumable?.remarks ?? ""),
      estimatedCost: isNoAmount
        ? 0
        : Number(reqUnitPrice) * Number(quantity ?? 1),
      name: consumable?.procedureName ?? consumable?.name,
      procedureName: consumable?.procedureName ?? consumable?.name,
      isDelete: false,
      serviceId,
      tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0) * Number(quantity ?? 1),
      total: isNoAmount ? 0 : Number(reqUnitPrice) * Number(quantity ?? 1),
      mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
      procedureVariantId: reqVar?.variantId ?? reqProced?.procedureVariantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? consumable?.packageUom ?? null,
      expiryDate: reqBat?.expiryDate ?? consumable?.expiryDate ?? null,
      batchNo:
        values?.batchNumber?.name ||
        reqBat?.batchNo ||
        consumable?.batchNo ||
        null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: "N",
      billSeparated: !!values?.billedSeparately,
      billType: reqStock?.autoConsumed
        ? currLocat?.category
          ? billingTabEnum?.[currLocat?.category]
          : "treatment"
        : "treatment",
      uomId: matchedUom?.uomMappingId ?? consumable?.uomId ?? null,
      uomName: matchedUom?.value ?? consumable?.uomName ?? null,
      reqProced,
      reqServ,
      reqVar,
      billing: consumable?.billing ?? false,
      locationId:
        values?.batchNumber?.locationId ??
        reqBat?.locationId ??
        currLocat?.id ??
        null,
      locationName:
        values?.batchNumber?.locationName ?? currLocat?.locationName ?? null,
    };
  };

  const handleSave = () => {
    if (!consumable) return;

    const detailPayload = buildConsumableDetail();
    const { reqProced, reqServ, reqVar, ...reqObj } = detailPayload;

    if (!reqProced?.procedureId) return;

    let tax = 0;
    let total = reqVar?.servicePrice ?? reqProced?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    setIsSaving(true);
    dispatch(
      updateProcedureByIdWoLoader({
        id: reqProced?.procedureId,
        data: {
          serviceCharge: reqProced?.serviceCharge,
          serviceType: reqProced?.serviceType,
          appointmentId: reqDet?.appointment?.appointmentId,
          procedureDetails: [reqObj],
          tax: Number(Number(tax) * Number(reqProced?.quantity ?? 1)),
          total: Number(Number(total) * Number(reqProced?.quantity ?? 1)),
          mrpPrice: Number(reqVar?.servicePrice ?? 0),
          procedureVariantId: reqVar?.variantId,
          quantity: Number(reqProced?.quantity ?? 1),
          hsnSacCode: reqServ?.hsnSacCode ?? null,
          paymentId: reqDet?.paymentId ?? null,
        },
      }),
    ).then((res) => {
      setIsSaving(false);
      if (res?.payload) {
        onApiCall();
        onClose();
      }
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Edit item"
      modal
      modalWidth={40}
      reduceHeaderSize
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              ITEM
            </Typography>
            <TextField
              fullWidth
              size="small"
              disabled
              value={consumable?.procedureName ?? consumable?.name ?? ""}
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            />
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BATCH / LOT NO.
              </Typography>
              <CustomSelect
                list={getBatchesForSelect(
                  consumable?.stockId,
                  values?.batchNumber?.value ?? consumable?.batchId,
                  values?.batchNumber?.name ?? consumable?.batchNo,
                )}
                value={values?.batchNumber}
                handleChange={(e, value) => onChangeValue("batchNumber", value)}
                name="batchNumber"
                search
                isBatchNumber
                disabled={
                  !consumable?.stockId ||
                  clinicStocks?.find(
                    (stc) => stc?.stockId === consumable?.stockId,
                  )?.autoConsumed
                }
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BILLABLE QTY
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={values?.billableQty}
                onChange={(e) => onChangeValue("billableQty", e.target.value)}
                inputProps={{ min: 1 }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                UOM
              </Typography>
              <CustomSelect
                list={getUOMListByStockId(consumable?.stockId)}
                value={values?.uom}
                search
                handleChange={(e, value) => onChangeValue("uom", value)}
              />
            </Grid>
          </Grid>

        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#616161",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            Cancel
          </Button>
          <CustomButton
            text="Save changes"
            smallBtn
            disabled={isSaving}
            onClick={handleSave}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default PharmacyEditConsumableDrawer;
